import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { FilesDropZone, ListSkeleton, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiClient } from '../../../../state';
import { FontFilesList } from './FontFilesList';
import type { FontFormData, FontSource } from './types';

type FontFile = { file: File; weight: api.FontWeight };

/**
 * File upload controls for custom fonts.
 *
 * Renders a drop zone for font files and a list of uploaded files.
 */
export function FileUploadControls(props: {
    organizationId: string;
    form: UseFormReturn<FontFormData>;
}) {
    const { organizationId, form } = props;
    const [isUploadPending, setUploadPending] = React.useState(false);

    const fontSources = form.watch('fontSources');

    const setFontSources = (newFiles: FontSource[]) => {
        form.setValue('fontSources', newFiles, {
            shouldValidate: true,
        });
    };

    /**
     * Handle file selection from the drop zone
     */
    const handleSelectFiles = useEventCallback(async (files: File[]) => {
        setUploadPending(true);

        try {
            const toUploadFiles: FontFile[] = files.map((file) => ({
                file,
                weight: detectFontWeightFromFileName(file.name),
            }));

            // Upload the files to storage
            const uploadedFontFiles = await uploadFilesToStorage(toUploadFiles, organizationId);

            if (uploadedFontFiles.length === 0) {
                throw new Error();
            }

            setFontSources([...fontSources, ...uploadedFontFiles]);
        } catch {
            toast.error('Failed to upload font files', null);
        }

        setUploadPending(false);
    });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <FontFilesList
                    fontSources={fontSources}
                    setFontSources={setFontSources}
                    formControl={form.control}
                />
                {isUploadPending ? <ListSkeleton items={1} excludeParts={['indicator']} /> : null}
            </div>
            <FilesDropZone
                acceptedFileTypes="fonts"
                onSelectFiles={handleSelectFiles}
                label="Drop your font files or browse (.woff, .woff2)"
            />
        </div>
    );
}

/**
 * Upload font files to R2 storage and return successfully uploaded files.
 */
async function uploadFilesToStorage(
    files: FontFile[],
    organizationId: string
): Promise<FontSource[]> {
    // 1. Generate presigned URLs for all files
    const uploadTasks = await Promise.all(
        files.map((file) => generatePresignedUrlForFile(file, organizationId))
    );

    // 2. Perform uploads  to storage
    return (await Promise.all(uploadTasks.map(uploadFileWithPresignedUrl))).filter(
        filterOutNullable
    );
}

/**
 * Generate a presigned URL for a file and return upload information.
 */
async function generatePresignedUrlForFile(
    fontFile: FontFile,
    organizationId: string
): Promise<{
    originalFile: FontFile;
    uploadUrl: string;
    storageKey: string;
}> {
    const { file } = fontFile;

    const response = await apiClient.storage.generateStorageUploadUrl(organizationId, {
        file: {
            name: file.name,
            type: detectFontTypeFromFileName(file.name),
            size: file.size,
        },
        kind: api.StorageUploadKind.CustomizationFont,
    });

    return {
        originalFile: fontFile,
        storageKey: response.data.key,
        uploadUrl: response.data.url,
    };
}

/**
 * Upload a file using its presigned URL and return the result.
 */
async function uploadFileWithPresignedUrl(uploadTask: {
    originalFile: FontFile;
    uploadUrl: string;
    storageKey: string;
}): Promise<FontSource | null> {
    const { originalFile, uploadUrl, storageKey } = uploadTask;

    try {
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: originalFile.file,
        });

        if (!response.ok) {
            return null;
        }

        return {
            ...originalFile,
            storageKey,
        };
    } catch {
        return null;
    }
}

/**
 * Basic detecton of font weight based on filename
 */
function detectFontWeightFromFileName(fileName: string): api.FontWeight {
    const lowerCaseFileName = fileName.toLowerCase();

    if (lowerCaseFileName.includes('semibold')) {
        return 600;
    }

    if (lowerCaseFileName.includes('bold')) {
        return 700;
    }

    return 400;
}

/**
 * Detects font type based on filename
 */
function detectFontTypeFromFileName(fileName: string): string {
    if (fileName.endsWith('woff')) {
        return 'font/woff';
    }

    return 'font/woff2';
}
